import React, { useMemo, useState } from 'react';

const fmt = (n) => '£' + Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';

/**
 * Searchable invoice picker. The caller passes the full set of invoices
 * available for the current reconciliation run.
 */
const BOILERPLATE_RX = /\b(PAYMENT|PAYMENTS|TRANSFER|FROM|TO|THE|AND|BANK|LTD|LIMITED|LLC|INC|PLC|BY|FOR|OF|CO|BACS|CHAPS|FPS|DD|SO|REF|ID)\b/g;

function suggestPhrase(description) {
  if (!description) return '';
  const cleaned = description.toUpperCase().replace(BOILERPLATE_RX, '').replace(/[^A-Z0-9 ]+/g, ' ');
  const words = cleaned.split(/\s+/).filter(w => w.length >= 4 && !/^\d+$/.test(w));
  if (!words.length) return '';
  words.sort((a, b) => b.length - a.length);
  return words[0];
}

export default function InvoicePicker({ invoices, transaction, onPick, onCancel, onCreateRule }) {
  const [query, setQuery] = useState('');
  const [saveAsRule, setSaveAsRule] = useState(false);
  const [rulePhrase, setRulePhrase] = useState(() => suggestPhrase(transaction?.description || ''));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const txnAmt = transaction?.amount || 0;
    let list = invoices || [];

    if (q) {
      list = list.filter(inv => {
        const hay = `${inv.invoiceNumber || ''} ${inv.contactName || ''} ${inv.reference || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }

    // Sort: closest amount first, then by contact name
    return [...list].sort((a, b) => {
      const da = Math.abs((a.amountDue || 0) - txnAmt);
      const db = Math.abs((b.amountDue || 0) - txnAmt);
      if (da !== db) return da - db;
      return (a.contactName || '').localeCompare(b.contactName || '');
    }).slice(0, 50);
  }, [invoices, query, transaction]);

  const onSelect = (invoice) => {
    onPick(invoice, { saveAsRule, rulePhrase: saveAsRule ? rulePhrase : null });
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Pick a different invoice</h3>
          <button className="modal-close" onClick={onCancel} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          <div className="modal-txn-summary">
            <strong>{transaction?.description}</strong> · {fmt(transaction?.amount)} · {fmtDate(transaction?.date)}
          </div>

          <input
            className="modal-search"
            type="search"
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by invoice number, contact, or reference…"
          />

          <div className="modal-results">
            {filtered.length === 0 && (
              <div className="empty-tab">No invoices match.</div>
            )}
            {filtered.map(inv => {
              const matches = Math.abs((inv.amountDue || 0) - (transaction?.amount || 0)) < 0.01;
              return (
                <button
                  key={inv.id}
                  className={`picker-row ${matches ? 'amount-match' : ''}`}
                  onClick={() => onSelect(inv)}
                >
                  <div className="picker-row-main">
                    <div className="picker-row-name">{inv.contactName || '(no contact)'}</div>
                    <div className="picker-row-meta">
                      {inv.invoiceNumber} · due {fmtDate(inv.dueDate)}
                      {inv.reference ? ` · ref ${inv.reference}` : ''}
                    </div>
                  </div>
                  <div className="picker-row-amount">{fmt(inv.amountDue)}</div>
                </button>
              );
            })}
          </div>

          {onCreateRule && (
            <div className="modal-rule-block">
              <label className="modal-rule-toggle">
                <input
                  type="checkbox"
                  checked={saveAsRule}
                  onChange={e => setSaveAsRule(e.target.checked)}
                />
                <span>
                  Also save as a rule — future transactions whose description
                  contains the phrase below will be auto-coded to this contact.
                </span>
              </label>
              {saveAsRule && (
                <div className="modal-rule-phrase">
                  <label>Match phrase</label>
                  <input
                    type="text"
                    value={rulePhrase}
                    onChange={e => setRulePhrase(e.target.value)}
                    placeholder="e.g. STRIPE"
                  />
                  <p className="modal-rule-hint">
                    Pick a short, distinctive string — too generic ("PAYMENT") will
                    match transactions that don't belong here.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
